import { __rest } from "tslib";
import { jsx as _jsx } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useReducer, useRef } from 'react';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { useFormValidator } from '../../../hooks/use-form-validator';
import { validateField } from '../../../utils/form/validate-field';
import { ButtonSize } from '../../button';
import { Button } from '../../button/button';
import { ButtonColor } from '../../button/types/color';
import { TextAreaField } from '../../form/field/text-area/text-area-field';
import { Form } from '../../form/form/form';
import { defaultFormCancelButtonConfig, defaultFormSubmitButtonConfig } from '../../form/form/types/config';
import { Modal } from '../../modal/modal';
import { TextInputModalActionType as ActionType } from '../types/action-type';
import { defaultTextInputModalState } from '../types/state';
import { textInputModalActionReducer } from '../utils/action-reducer';
const b = bemClassBuilder('v-text-input-modal');
export const TextInputModalUi = forwardRef((props, ref) => {
    const { className, heading, value, validationConfig, submitLabel, label, showLabel, requiredMessage, lengthMessage, show, onChangeValue, onClose, onOpen, onSubmitButtonClick } = props, modalProps = __rest(props, ["className", "heading", "value", "validationConfig", "submitLabel", "label", "showLabel", "requiredMessage", "lengthMessage", "show", "onChangeValue", "onClose", "onOpen", "onSubmitButtonClick"]);
    const formRef = useRef(null);
    const formId = useId();
    const [state, dispatch] = useReducer(textInputModalActionReducer, defaultTextInputModalState);
    const { formData } = state;
    const { validationResult, validator, setValidationResult } = useFormValidator(validationConfig);
    const { form: formConfig } = useOccConfig();
    const textAreaRef = useRef(null);
    const attemptSubmit = useCallback(async () => {
        if (!formRef.current) {
            return false;
        }
        return await formRef.current.attemptSubmit();
    }, []);
    useImperativeHandle(ref, () => ({
        attemptSubmit
    }));
    useEffect(() => {
        dispatch({ type: ActionType.SetFormData, formData: { input: value } });
    }, [value, show]);
    const handleOpenModal = useCallback(() => {
        var _a;
        (_a = textAreaRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        onOpen === null || onOpen === void 0 ? void 0 : onOpen();
    }, [onOpen]);
    const handleCloseModal = useCallback(() => {
        setValidationResult({ isValid: true, validationErrors: {} });
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [onClose, setValidationResult]);
    const handleValueChanged = useCallback((newValue) => {
        dispatch({ type: ActionType.SetFormData, formData: { input: newValue } });
    }, []);
    const handleSubmit = useCallback(async () => {
        if (!validationResult.isValid) {
            return;
        }
        await (onChangeValue === null || onChangeValue === void 0 ? void 0 : onChangeValue(formData.input));
        handleCloseModal();
    }, [validationResult.isValid, formData.input, handleCloseModal, onChangeValue]);
    const handleValidate = useCallback(async (newValue) => {
        const result = await validateField(formConfig, validationConfig.input, newValue);
        setValidationResult({ isValid: !result, validationErrors: { input: result } });
        return result;
    }, [formConfig, setValidationResult, validationConfig.input]);
    const handleFormSubmittingStateChanged = useCallback((isSubmitting) => {
        dispatch({ type: ActionType.SetFormSubmittingState, isSubmitting });
    }, []);
    return (_jsx(Modal, Object.assign({}, modalProps, { className: b.block(undefined, className), show: show, heading: heading, onOpen: handleOpenModal, onClose: handleCloseModal, canBeClosed: !state.isSubmitting, footer: _jsx(Button, { label: submitLabel, className: b.element('submit-button'), formId: formId, loading: state.isSubmitting, type: "submit", color: ButtonColor.Primary, size: ButtonSize.Large, onClick: onSubmitButtonClick }), children: _jsx(Form, { id: formId, ref: formRef, className: b.element('form'), config: {
                cancelButton: Object.assign(Object.assign({}, defaultFormCancelButtonConfig), { show: false }),
                submitButton: Object.assign(Object.assign({}, defaultFormSubmitButtonConfig), { show: false })
            }, validator: () => validator.validateForm(formData), events: {
                onSubmit: handleSubmit,
                onFormSubmittingStateChanged: handleFormSubmittingStateChanged
            }, setFormData: getMergedData => {
                dispatch({ type: ActionType.SetFormData, formData: getMergedData(formData) });
            }, children: _jsx(TextAreaField, { textAreaRef: textAreaRef, id: b.element('input'), className: b.element('input'), value: formData.input, onChangeValue: handleValueChanged, displayText: { label, requiredMessage, lengthMessage }, validate: handleValidate, validationConfig: validationConfig.input, error: validationResult.validationErrors.input, disabled: state.isSubmitting, showLabel: showLabel }) }) })));
});
TextInputModalUi.displayName = 'TextInputModalUi';
//# sourceMappingURL=ui.js.map